import styled from 'styled-components';

export const CardContainer = styled.div`
  background: var(--bg-light);
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-lg);
  margin-bottom: var(--spacing-2xl);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: var(--shadow-lg);
    border-color: var(--gray-300);
  }

  /* Responsividade para telas menores */
  @media (max-width: 991px) {
    margin-bottom: var(--spacing-lg);
  }
`;

export const CardHeader = styled.div`
  background: var(--primary-color);
  color: var(--text-light);
  padding: var(--spacing-lg) var(--spacing-xl);
  border-bottom: 1px solid var(--gray-200);
  
  h5 {
    margin: 0;
    font-weight: var(--font-weight-semibold);
    font-size: var(--font-size-lg);
    color: var(--text-light);
  }
`;

export const CardBody = styled.div`
  padding: var(--spacing-2xl);
  background: var(--bg-light);
  flex: 1;
`;

export const LoadingCardContainer = styled.div`
  background: var(--bg-light);
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-lg);
  margin-bottom: var(--spacing-2xl);
  box-shadow: var(--shadow-sm);
  padding: var(--spacing-4xl);
  text-align: center;
  
  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-lg);
  }
  
  .loading-text {
    color: var(--text-secondary);
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-medium);
    margin: 0;
  }
  
  .spinner-border {
    width: 2rem;
    height: 2rem;
  }
`;

export const FormGroup = styled.div`
  margin-bottom: var(--spacing-lg);
  
  label {
    font-weight: var(--font-weight-semibold);
    color: var(--primary-color);
    margin-bottom: var(--spacing-sm);
    font-size: var(--font-size-sm);
  }
`;

export const TableContainer = styled.div`
  margin-top: var(--spacing-lg);
  border-radius: var(--border-radius-md);
  overflow: visible;
  border: 1px solid var(--gray-200);
  
  .table {
    margin-bottom: 0;
    font-size: var(--font-size-sm);

    th {
      background-color: var(--primary-color);
      border-top: none;
      color: var(--text-light);
      font-weight: var(--font-weight-semibold);
      padding: var(--spacing-lg);
      border-bottom: 1px solid var(--gray-300);
    }

    td {
      padding: var(--spacing-lg);
      vertical-align: middle;
      color: var(--text-muted);
      border-bottom: 1px solid var(--gray-100);
    }
    
    tbody tr:hover {
      background-color: var(--gray-50);
    }
  }
`;

export const InfoBadge = styled.div`
  background-color: var(--gray-100);
  color: var(--text-secondary);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  display: inline-block;
`;

export const SectionTitle = styled.h6`
  color: var(--primary-color);
  font-weight: var(--font-weight-semibold);
  margin-bottom: var(--spacing-lg);
  padding-bottom: var(--spacing-sm);
  border-bottom: 2px solid var(--gray-200);
  font-size: var(--font-size-base);
`;

export const ActionSection = styled.div`
  margin-top: var(--spacing-3xl);
  padding-top: var(--spacing-2xl);
  border-top: 1px solid var(--gray-200);
  
  .btn-group {
    display: flex;
    gap: var(--spacing-md);
    flex-wrap: wrap;
  }
`;

export const CompactCard = styled(CardContainer)`
  /* Estilos especiais para cards compactos em layout de colunas */
  .compact-layout {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }
  
  .compact-row {
    display: flex;
    gap: var(--spacing-md);
    flex-wrap: wrap;
    
    > * {
      flex: 1;
      min-width: 200px;
    }
  }
  
  @media (max-width: 768px) {
    .compact-row {
      flex-direction: column;
      gap: var(--spacing-sm);
    }
  }
`;

export const FileUploadArea = styled.div`
  border: 2px dashed var(--gray-300);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-2xl);
  margin: var(--spacing-lg) 0;
  background-color: var(--gray-50);
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--primary-color);
    background-color: var(--bg-light);
  }
  
  &.dragover {
    border-color: var(--primary-color);
    background-color: var(--primary-color-light);
  }
  
  .upload-icon {
    font-size: var(--font-size-3xl);
    color: var(--gray-400);
    margin-bottom: var(--spacing-md);
  }
  
  .upload-text {
    color: var(--text-secondary);
    font-size: var(--font-size-sm);
  }
`;

export const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md);
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-md);
  margin-bottom: var(--spacing-sm);
  background-color: var(--bg-light);
  
  &:hover {
    background-color: var(--gray-50);
  }
  
  .file-info {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
    
    .file-name {
      font-weight: var(--font-weight-medium);
      color: var(--text-primary);
    }
    
    .file-size {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
    }
  }
  
  .file-actions {
    display: flex;
    gap: var(--spacing-sm);
  }
`;

export const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${props => 
      props.status === 'success' ? 'var(--success-color)' :
      props.status === 'warning' ? 'var(--warning-color)' :
      props.status === 'error' ? 'var(--danger-color)' :
      'var(--gray-400)'
    };
  }
  
  .status-text {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
  }
`;

export const CardRow = styled.div`
  margin-bottom: var(--spacing-2xl);
  
  .row {
    display: flex;
    align-items: stretch;
  }
  
  .col-lg-6, .col-md-12 {
    margin-bottom: var(--spacing-lg);
    display: flex;
    
    @media (min-width: 992px) {
      margin-bottom: 0;
    }
  }
  
  /* Cards dentro de colunas devem ter altura igual */
  .col-lg-6 > div,
  .col-md-12 > div {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  
  /* Último card row não precisa de margin bottom */
  &:last-child {
    margin-bottom: 0;
  }
`;
